import { FC, ReactNode, ElementType, MouseEvent, useCallback, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { css, useTheme, Theme } from '@emotion/react';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemSecondaryAction from '@mui/material/ListItemSecondaryAction';
import MuiMenuItem from '@mui/material/MenuItem';
import { ArrowRight, ArrowSquareOut, CheckCircle, CheckSquare, Circle, Spinner, Square } from '@phosphor-icons/react';
import { spinIconStyle } from 'client/components/Button/Button';
import type { AvatarProps } from 'client/components/Other/Avatar';
import { ListItemFigure, ListItemFigureProps } from 'client/components/Other/ListItemFigure';
import { ListItemText } from 'client/components/Other/ListItemText';
import type { TypographyProps } from 'client/components/Typography';
import { borderRadius } from 'client/Theme';

export const ActionIcon: FC<{ selected: boolean; loading?: boolean; checkbox?: boolean; icon?: JSX.Element | null; size?: number }> = ({
  selected,
  loading = false,
  checkbox = false,
  icon = null,
  size = 24,
}) => {
  const theme = useTheme();
  const color = selected ? theme.color.primary[100] : theme.color.grey[80];

  const iconProps = { size, color };

  if (loading) {
    return <Spinner {...iconProps} css={spinIconStyle} />;
  }

  if (icon) {
    return <icon.type {...iconProps} {...icon.props} />;
  }

  if (checkbox) {
    if (selected) return <CheckSquare {...iconProps} weight="fill" />;
    return <Square {...iconProps} weight="bold" />;
  }

  if (selected) return <CheckCircle {...iconProps} weight="fill" />;

  return <Circle {...iconProps} weight="bold" />;
};

const ToggleableMenuItem: FC<MenuItemProps & { checked: boolean }> = ({ title: _, checked, onSelect: __, children, ...props }) => (
  <ListItem selected={checked} secondaryAction={<ActionIcon selected={checked} size={18} checkbox />} disablePadding {...props}>
    <ListItemButton disableRipple role="checkbox">
      {children}
    </ListItemButton>
  </ListItem>
);

type MenuItemLink = {
  id: string;
  title: string;
  icon?: JSX.Element;
  endIcon?: JSX.Element;
  href?: string;
  onClick?: (event: MouseEvent<HTMLLIElement>, id: string) => void;
};

export interface MenuItemProps {
  id: string;
  title: string | JSX.Element;
  subtitle?: string | JSX.Element | null;
  subtitleProps?: TypographyProps;
  icon?: JSX.Element;
  iconRight?: JSX.Element;
  image?: AvatarProps;
  ornament?: ListItemFigureProps['ornament'];
  component?: ElementType;
  href?: string;

  small?: boolean;
  toggleable?: boolean;
  external?: boolean;
  absolute?: boolean;
  selected?: boolean;
  disabled?: boolean;

  /** Will render a list of links after this menu item */
  links?: MenuItemLink[];

  children?: ReactNode;
  onSelect?: (id: string) => void;
}

export const MenuItem: FC<MenuItemProps> = ({
  id,
  title,
  subtitle = null,
  subtitleProps,
  icon = null,
  iconRight = null,
  image = null,
  ornament = null,
  component = 'li',
  href = null,
  small = false,
  toggleable = false,
  external = false,
  absolute = false,
  disabled = false,
  selected,
  links = null,
  onSelect,
}) => {
  const theme = useTheme();
  const labelID = useMemo(() => `select-${id}-label`, [id]);

  const handleSelectLink = useCallback((id: string) => !disabled && onSelect && onSelect(id), [disabled, onSelect]);
  const handleSelectItem = useCallback(() => !disabled && onSelect && onSelect(id), [id, disabled, onSelect]);

  const renderLink = useCallback(
    (link: MenuItemLink) => {
      const item = (
        <MuiMenuItem
          key={link.id}
          sx={{ borderRadius: `${borderRadius}px` }}
          css={linkStyle}
          className="link"
          onClick={(event) => (link.onClick ? link.onClick(event, link.id) : handleSelectLink(link.id))}
          disabled={disabled}
        >
          <ListItemText
            primary={
              <>
                {link.icon ? <link.icon.type weight="fill" {...link.icon.props} className="icon" /> : null}
                {link.title}
                {link.endIcon || <ArrowRight color={theme.color.grey[100]} weight="bold" />}
              </>
            }
            primaryProps={{
              variant: 'body2',
            }}
          />
        </MuiMenuItem>
      );

      if (!disabled && link.href) {
        return (
          <Link key={link.id} to={link.href} css={menuItemLinkStyle}>
            {item}
          </Link>
        );
      }

      return item;
    },
    [disabled, handleSelectLink]
  );

  const listItemElement = toggleable ? (
    <ToggleableMenuItem id="" title="" checked={!!selected} />
  ) : (
    <MuiMenuItem component={component} selected={selected} />
  );

  let menuItem = (
    <listItemElement.type
      id={id}
      {...listItemElement.props}
      key={id}
      sx={{ borderRadius: `${borderRadius}px` }}
      css={listItemStyle}
      disabled={disabled}
      onClick={handleSelectItem}
    >
      <ListItemFigure image={image} icon={icon} ornament={ornament} css={figureStyle} />
      <ListItemText id={labelID} primary={title} secondary={!small && subtitle} secondaryProps={subtitleProps} />
      {(external || !!iconRight) && (
        <ListItemSecondaryAction>
          {iconRight ? (
            <iconRight.type size={22} color={theme.color.grey[80]} {...iconRight.props} />
          ) : external ? (
            <ArrowSquareOut size={22} color={theme.color.grey[80]} />
          ) : null}
        </ListItemSecondaryAction>
      )}
    </listItemElement.type>
  );

  if (href) {
    if (external) {
      menuItem = (
        <a css={menuItemLinkStyle} href={href} target="_blank" onClick={handleSelectItem}>
          {menuItem}
        </a>
      );
    } else {
      if (absolute) {
        menuItem = (
          <a css={menuItemLinkStyle} href={href}>
            {menuItem}
          </a>
        );
      } else {
        menuItem = (
          <Link css={menuItemLinkStyle} to={href} onClick={handleSelectItem}>
            {menuItem}
          </Link>
        );
      }
    }
  }

  if (links) {
    return (
      <>
        {menuItem} {links.map(renderLink)}
      </>
    );
  }

  return menuItem;
};

const listItemStyle = (theme: Theme) => css`
  margin-bottom: 2px;
  overflow: hidden;

  &:hover {
    background-color: ${theme.color.grey[60]};
  }

  > .MuiListItemButton-root {
    padding-top: 0;
    padding-bottom: 0;
  }

  &,
  > .MuiListItemButton-root {
    > .MuiListItemText-root {
      > .MuiListItemText-primary {
        font-weight: bold;
      }
    }

    > .MuiListItemSecondaryAction-root {
      display: flex;
    }

    &.MuiListItem-root.Mui-selected {
      background-color: transparent;
    }

    > .MuiListItemIcon-root {
      min-width: 36px;
    }
  }
`;

const figureStyle = css`
  margin-right: 12px;

  > img {
    margin-right: 4px;
  }
`;

const linkStyle = (theme: Theme) => css`
  padding-left: 59px;

  &:hover {
    background-color: ${theme.color.grey[60]};
  }

  .MuiListItemText-root {
    .typography {
      display: flex;
      align-items: center;

      .icon {
        margin-right: 0.4rem;
        margin-left: 0;
        color: ${theme.color.primary[100]};
      }

      svg {
        margin-left: 0.4rem;
      }
    }
  }
`;

const menuItemLinkStyle = css`
  text-decoration: none;
  color: inherit;
`;
